import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, AreaChart, Area, CartesianGrid, LabelList
} from 'recharts';

const TEAL = '#2f9e8e';
const TEAL_LIGHT = '#7fd6c9';
const GOLD = '#c19a3c';
const GOLD_LIGHT = '#e0c674';
const NAVY = '#1a365d';
const NAVY_DEEP = '#0a1729';
const SLATE = '#94a3b8';

// shared dark tooltip
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-[#0a1729]/95 border border-white/15 px-3 py-2 text-xs shadow-2xl backdrop-blur-md">
      {label && <div className="text-white/60 uppercase tracking-wider text-[10px] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-white font-medium">{p.name}:</span>
          <span className="text-[#7fd6c9] font-bold">{p.value}{typeof p.value === 'number' && p.value < 1000 ? 'B' : ''}</span>
        </div>
      ))}
    </div>
  );
};

const LightTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs shadow-xl">
      {label && <div className="text-gray-500 uppercase tracking-wider text-[10px] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-[#1a365d] font-medium">{p.name}:</span>
          <span className="text-[#2f9e8e] font-bold">${p.value}B</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Horizontal bar of $B amounts.
 * data: [{ name, value (number, in $B) }]
 */
export const CapitalBars = ({ data, color = TEAL, theme = 'dark', height = 320 }) => {
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#94a3b8' : '#64748b';
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 38, left: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} />
        <XAxis type="number" hide />
        <YAxis dataKey="name" type="category" width={210} tick={{ fill: axisColor, fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: isDark ? 'rgba(127,214,201,0.06)' : 'rgba(47,158,142,0.07)' }} content={isDark ? <DarkTooltip /> : <LightTooltip />} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} fill={color}>
          <LabelList dataKey="value" position="right" formatter={(v) => `$${v}B`} style={{ fill: isDark ? '#e2e8f0' : '#1a365d', fontSize: 11, fontWeight: 700 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Donut chart for platform allocation.
 * data: [{ name, value }]
 */
export const PlatformDonut = ({ data, height = 320 }) => {
  const colors = [TEAL, TEAL_LIGHT, GOLD, GOLD_LIGHT, '#5dbfb0', '#a8842f', NAVY, '#3b82a6'];
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius="58%"
          outerRadius="92%"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={2}
          paddingAngle={1.5}
        >
          {data.map((d, i) => (
            <Cell key={d.name} fill={colors[i % colors.length]} />
          ))}
        </Pie>
        <Tooltip content={<DarkTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};

/**
 * Roadmap cumulative deployment area chart by phase.
 * data: [{ phase, value }]
 */
export const RoadmapArea = ({ data, theme = 'dark', height = 280 }) => {
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#94a3b8' : '#64748b';
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 12, right: 20, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="roadmap-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={TEAL_LIGHT} stopOpacity={0.5} />
            <stop offset="100%" stopColor={TEAL} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} vertical={false} />
        <XAxis dataKey="phase" tick={{ fill: axisColor, fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: axisColor, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}B`} />
        <Tooltip content={isDark ? <DarkTooltip /> : <LightTooltip />} />
        <Area type="monotone" dataKey="value" stroke={TEAL_LIGHT} strokeWidth={2.5} fill="url(#roadmap-grad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/**
 * Section header chip for a chart card.
 */
export const ChartHeader = ({ eyebrow, title, light = false }) => (
  <div className="mb-4">
    {eyebrow && <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-1 ${light ? 'text-[#7fd6c9]' : 'text-[#2f9e8e]'}`}>{eyebrow}</div>}
    <h3 className={`font-serif text-lg font-bold ${light ? 'text-white' : 'text-[#1a365d]'}`}>{title}</h3>
  </div>
);
